import MiniMarkdown from '../../utils/miniMarkdown';
import DebriefTrackMap from './DebriefTrackMap';
import styles from './DetailedSessionAnalysisContent.module.css';

export default function DetailedSessionAnalysisContent({ analysis, trackMapData }) {
  if (!analysis) return null;
  if (analysis.status === 'error') {
    return <div className={styles.error}>The deep analysis is unavailable: {analysis.error}</div>;
  }

  const meta = analysis.meta;
  const limitedSessionSample = meta?.cleanLapCount < 3;
  const limitedHistory = meta?.nSessions < 4;
  const confidenceLabel = meta?.confidence === 'limited'
    && limitedSessionSample && !limitedHistory
    ? `${meta.cleanLapCount}-lap sample`
    : `${meta?.confidence || 'limited'} evidence`;
  const activeCorners = (meta?.priorityCorners || [])
    .filter(c => c.priority_role && c.priority_role !== 'watch');
  const waiting = ['queued', 'building'].includes(analysis.status) && !analysis.text;
  if (waiting) {
    return (
      <div className={styles.working} aria-live="polite">
        <span className={styles.scan} aria-hidden="true"><i /><i /><i /><i /></span>
        <div>
          <strong>{analysis.status === 'queued' ? 'Waiting for Quick Debrief' : 'Building deep evidence'}</strong>
          <span>Comparing representative laps, corner technique, conditions, and your own history.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      {meta && (
        <>
          <div className={styles.summary}>
            <div className={styles.summaryCopy}>
              <span className={`${styles.confidence} ${styles[meta.confidence] || ''}`}>
                {confidenceLabel}
              </span>
              <strong>{(activeCorners.length || meta.priorityCorners.length)} technique priorit{(activeCorners.length || meta.priorityCorners.length) === 1 ? 'y' : 'ies'}</strong>
              <span>{meta.cleanLapCount} clean lap{meta.cleanLapCount === 1 ? '' : 's'} · {meta.nSessions} sessions here</span>
              {!!meta.availableChannels.length && (
                <small>Measured with {meta.availableChannels.join(', ')}</small>
              )}
            </div>
            {!!meta.priorityCorners.length && (
              <DebriefTrackMap
                {...trackMapData}
                gaps={activeCorners.length ? activeCorners : meta.priorityCorners.slice(0, 2)}
                sectorGaps={[]}
              />
            )}
          </div>
          {meta.confidence === 'limited' && (
            <div className={styles.limited}>
              {limitedSessionSample && !limitedHistory ? (
                <>Limited session sample: only {meta.cleanLapCount} representative clean lap{meta.cleanLapCount === 1 ? '' : 's'} qualified from this session, so its corner findings are tentative. Historical comparisons still draw on {meta.nDays} days and {meta.nSessions} sessions here.</>
              ) : limitedHistory && !limitedSessionSample ? (
                <>Limited comparison history: historical trends are provisional, while this session's {meta.cleanLapCount} representative clean laps still support within-session corner findings.</>
              ) : (
                <>Limited evidence: only {meta.cleanLapCount} representative clean lap{meta.cleanLapCount === 1 ? '' : 's'} and {meta.nSessions} historical session{meta.nSessions === 1 ? '' : 's'} qualified for this track.</>
              )}
            </div>
          )}
        </>
      )}
      {analysis.validationWarning && (
        <div className={styles.warning}>{analysis.validationWarning}</div>
      )}
      <GenerationStatus status={analysis.status} />
      <MiniMarkdown text={analysis.text} className={styles.report} />
      <WatchList corners={meta?.priorityCorners} />
    </div>
  );
}

// The measured sections land complete and instantly, because Python composed
// them; only the coach's narrative streams in around them. So the report looks
// finished long before it is, and then visibly rewrites itself -- which reads as
// a glitch rather than as progress. This says which phase is running and, more
// usefully, which part of what you are already reading is still moving.
const PHASES = {
  building: {
    label: 'Reading the telemetry',
    detail: 'Comparing representative laps, corner technique, and your own history.',
  },
  queued: {
    label: 'Waiting for the Quick Debrief to finish',
    detail: 'Deep Analysis starts as soon as it completes.',
  },
  streaming: {
    label: 'Writing the coach\u2019s read',
    detail: 'The measured sections below are final. The narrative is still being written.',
  },
  correcting: {
    label: 'Checking every number against the measured evidence',
    detail: 'Anything the data does not support is removed before you see it.',
  },
};

function GenerationStatus({ status }) {
  const phase = PHASES[status];
  if (!phase) return null;          // done / error / idle -- nothing to report
  return (
    <div className={styles.phase} role="status" aria-live="polite">
      <span className={styles.pulse} aria-hidden="true"><i /><i /><i /></span>
      <div>
        <strong>{phase.label}</strong>
        <span>{phase.detail}</span>
      </div>
    </div>
  );
}

// Everything the engine found beyond the two things worth working on. Collapsed
// by design: the report deliberately coaches two corners, and putting the rest
// in the open would undo that. They stay reachable because a driver who asks
// "what about Turn 6?" deserves an answer.
function WatchList({ corners }) {
  const watched = (corners || []).filter(c => c.priority_role === 'watch');
  if (!watched.length) return null;
  return (
    <details className={styles.watchList}>
      <summary>
        Also noted, not this session&rsquo;s focus ({watched.length})
      </summary>
      <ul>
        {watched.map(corner => (
          <li key={corner.corner_id || corner.label}>
            <strong>{corner.label}</strong>
            {corner.summary ? <span> — {corner.summary}</span> : null}
            {corner.confidence_level && corner.confidence_level !== 'firm' && (
              <em> ({corner.confidence_level} evidence)</em>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
